import { useApiInstance } from "@/providers/api-provider";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import createSelectors from "./create-selectors";

type Org = {
  id: string;
  name: string;
  logoUrl?: string;
};

type OrgUser = {
  id: string;
  email: string;
  name: string;
};

type OrgStoreState = {
  selectedOrgId: string | null;
  selectedOrg: Org | null;
  orgs: Org[];
  licenseOrgs: Org[];
  fetchUserOrgs: () => Promise<Org[]>;
  fetchOrgsForLicenses: () => Promise<Org[]>;
  fetchOrgUsersForLicense: (orgId: string) => Promise<OrgUser[]>;
  setSelectedOrg: (orgId: string) => void;
  setOrg: (org: Org) => void;
};

const useOrgStoreBase = create<OrgStoreState>()(
  persist(
    (set, get) => ({
      selectedOrgId: null,
      selectedOrg: null,
      orgs: [],
      licenseOrgs: [],
      setSelectedOrg: (orgId) => {
        const org = get().orgs.find((o) => o.id === orgId);
        set({ selectedOrgId: orgId, selectedOrg: org || null });
      },
      setOrg: (org) => set({ selectedOrg: org, selectedOrgId: org.id }),
      fetchUserOrgs: async () => {
        const response = await useApiInstance.get("/api/v1/organizations/my-orgs");
        const orgs = response.data?.data || [];
        set({ orgs });

        if (!get().selectedOrgId && orgs.length > 0) {
          set({ selectedOrgId: orgs[0].id, selectedOrg: orgs[0] });
        } else if (get().selectedOrgId) {
          const currentOrg = orgs.find((o: Org) => o.id === get().selectedOrgId);
          if (currentOrg) {
            set({ selectedOrg: currentOrg });
          } else if (orgs.length > 0) {
            set({ selectedOrgId: orgs[0].id, selectedOrg: orgs[0] });
          } else {
            set({ selectedOrgId: null, selectedOrg: null });
          }
        }

        console.info("Fetched user orgs:", response.data);
        return response.data;
      },
      fetchOrgsForLicenses: async () => {
        const response = await useApiInstance.get("/api/v1/licenses/organizations");
        set({ licenseOrgs: response.data });
        console.info("Fetched license orgs:", response.data);
        return response.data;
      },
      fetchOrgUsersForLicense: async (orgId: string) => {
        const response = await useApiInstance.get(`/api/v1/licenses/organizations/${orgId}/users`);
        console.info("Fetched org users for license:", response.data);
        return response.data;
      },
    }),
    {
      name: "org-context-storage",
      partialize: (state) => ({
        selectedOrgId: state.selectedOrgId,
        selectedOrg: state.selectedOrg,
      }),
    },
  ),
);

const useOrgStore = createSelectors(useOrgStoreBase);
export { Org, OrgUser, useOrgStore };
